import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { publicApiClient, certificateDownloadUrl, certificatePhotoUrl } from '@/api/public'
import { CheckCircle, AlertCircle, Clock, Infinity, ShieldCheck, Download, UserCheck } from 'lucide-react'
import { useState } from 'react'
import { datesFor } from '@/lib/dates'

function StatusBadge({ status }: { status: string }) {
  const config = {
    Valid:    { icon: CheckCircle,  label: 'Valid',     bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200' },
    DueSoon:  { icon: Clock,        label: 'Due soon',  bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200' },
    Overdue:  { icon: AlertCircle,  label: 'Overdue',   bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200' },
    NoExpiry: { icon: Infinity,     label: 'No expiry', bg: 'bg-blue-50',     text: 'text-blue-700',    border: 'border-blue-200' },
  }[status] ?? { icon: CheckCircle, label: status, bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' }

  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${config.bg} ${config.text} ${config.border}`}>
      <Icon size={15} />
      {config.label}
    </span>
  )
}

const PHOTO_SOURCE_LABEL: Record<string, string> = {
  Self: 'Photo added by the certificate holder',
  Trainer: 'Photo taken by the trainer at the course',
  Admin: 'Photo supplied by the training provider',
  ClientManager: 'Photo supplied by the holder’s employer',
}

export default function VerifyPage() {
  const { certificateId } = useParams<{ certificateId: string }>()
  const [photoFailed, setPhotoFailed] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['verify-certificate', certificateId],
    queryFn: () => publicApiClient.verifyCertificate(certificateId!).then(r => r.data),
    enabled: !!certificateId,
    retry: false,
  })

  const primaryColour = data?.companyPrimaryColour ?? '#4F46E5'
  const { formatDate: fmtDate } = datesFor(data?.region, data?.timeZoneId)
  const formatDate = (dateStr: string) => fmtDate(dateStr, { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f8f9fc' }}>
      {/* Branded header */}
      <header className="shadow-sm" style={{ backgroundColor: primaryColour }}>
        <div className="max-w-xl mx-auto px-6 py-5 flex items-center gap-4">
          {data?.companyLogoUrl && (
            <img
              src={data.companyLogoUrl}
              alt={data.companyName ?? 'Company logo'}
              className="h-10 w-auto object-contain shrink-0"
            />
          )}
          {data?.companyName && (
            <span className="text-white font-bold text-xl tracking-tight leading-tight">
              {data.companyName}
            </span>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-xl">
          {isLoading && (
            <div className="text-center text-gray-500 py-16 text-sm">Verifying certificate…</div>
          )}

          {isError && (
            <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertCircle className="text-red-500" size={28} />
                </div>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Certificate not found</h1>
              <p className="text-gray-500 text-sm">
                This certificate could not be verified. It may have been revoked or the link may be incorrect.
              </p>
            </div>
          )}

          {data && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Coloured top strip */}
              <div className="h-2" style={{ backgroundColor: primaryColour }} />

              <div className="p-8">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${primaryColour}18` }}>
                    <ShieldCheck size={32} style={{ color: primaryColour }} />
                  </div>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 text-center mb-1">
                  Certificate Verified
                </h1>
                <p className="text-sm text-gray-500 text-center mb-8">
                  This certificate has been verified as genuine.
                </p>

                {data.hasPhoto && !photoFailed && (
                  <div className="flex flex-col items-center mb-6">
                    <img
                      src={certificatePhotoUrl(certificateId!)}
                      alt={`Photo of ${data.recipientName}`}
                      onError={() => setPhotoFailed(true)}
                      referrerPolicy="no-referrer"
                      className="w-40 h-40 rounded-xl object-cover border border-gray-200"
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {data.photoSource ? PHOTO_SOURCE_LABEL[data.photoSource] : 'Photo on file'}
                    </p>
                    {data.identityCheckedOn && (
                      <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                        <UserCheck size={13} /> Identity checked by {data.identityCheckedByProvider ? 'the training provider' : 'the trainer'} on {formatDate(data.identityCheckedOn)}
                      </p>
                    )}
                  </div>
                )}

                <dl className="space-y-4">
                  <div className="flex justify-between items-start border-b border-gray-50 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Certificate holder</dt>
                    <dd className="text-sm font-semibold text-gray-900 text-right">{data.recipientName}</dd>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-50 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Course</dt>
                    <dd className="text-sm font-semibold text-gray-900 text-right">{data.courseName}</dd>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-50 pb-4">
                    <dt className="text-sm font-medium text-gray-500">Completed</dt>
                    <dd className="text-sm text-gray-900">{formatDate(data.completedDate)}</dd>
                  </div>
                  {data.expiryDate && (
                    <div className="flex justify-between items-start border-b border-gray-50 pb-4">
                      <dt className="text-sm font-medium text-gray-500">Expires</dt>
                      <dd className="text-sm text-gray-900">{formatDate(data.expiryDate)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd><StatusBadge status={data.status} /></dd>
                  </div>
                </dl>

                {data.status === 'Overdue' && (
                  <div className="mt-6 rounded-lg bg-red-50 border border-red-100 p-3 text-xs text-red-700 text-center">
                    This certificate has expired. Contact the training company to renew.
                  </div>
                )}

                <a
                  href={certificateDownloadUrl(certificateId!)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 flex items-center justify-center gap-2 w-full rounded-lg py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: primaryColour }}
                >
                  <Download size={16} /> Download certificate
                </a>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
          <ShieldCheck size={12} className="text-gray-300" />
          Verified by{' '}
          <a href="https://certslate.com" target="_blank" rel="noopener noreferrer"
            className="font-medium text-gray-500 hover:text-gray-700 transition-colors">
            CertSlate
          </a>
        </p>
      </footer>
    </div>
  )
}
